import { StyleSheet, Switch, Text, View } from 'react-native';
import React, { FC, useCallback, useMemo, useState } from 'react';
import {
  IPreferencesFormState,
  PreferencesComponent,
  PreferencesFormType,
} from '../types';
import { STYLE_VARS } from '../../../constants/style-vars';
import { Image } from '../../../components/Image';
import { Button } from '../../../components/Button';
import { useIsDesktop } from '../../../hooks/useIsDeskTop';
import { COLORS, FONTS } from '../../../constants';
import { decode } from 'html-entities';

interface PreferencesFormProps {
  form: PreferencesFormType;
  onSubmit?: (data: IPreferencesFormState[]) => void;
  initState: IPreferencesFormState[];
}

const PreferencesForm: FC<PreferencesFormProps> = ({
  form,
  onSubmit,
  initState,
}) => {
  const [state, setState] = useState(initState);
  const isDesktop = useIsDesktop();

  const renderRowSwitch = useCallback(
    (
      component: {
        title?: string;
        contents: PreferencesComponent[];
      },
      index: number
    ) => {
      return (
        <View key={`${component.title}-${component.contents.length}`}>
          {component.contents.map((content) => (
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 10,
              }}
              key={content.id}
            >
              <Text style={styles.componentLabel}>{decode(content.label)}</Text>
              <Switch
                trackColor={{
                  true: STYLE_VARS.primaryColor,
                  false: STYLE_VARS.fccDarkGrey,
                }}
                ios_backgroundColor={STYLE_VARS.fccLightGrey}
                thumbColor={STYLE_VARS.fccLightGrey}
                onValueChange={(value: boolean) => {
                  if (content.effectAll) {
                    const newState = [...state].map((item) => {
                      return {
                        ...item,
                        contents: Object.fromEntries(
                          Object.entries(item.contents).map(([key]) => [
                            key,
                            value,
                          ])
                        ),
                      };
                    });
                    setState(newState);
                  } else {
                    if (content.effectComponent) {
                      const newState = [...state].map((item) => {
                        if (item.title === state[index].title) {
                          return {
                            ...item,
                            contents: Object.fromEntries(
                              Object.entries(item.contents).map(([key]) => [
                                key,
                                value,
                              ])
                            ),
                          };
                        }
                        return item;
                      });
                      setState(newState);
                    } else {
                      const newState = [...state].map((item) => {
                        if (item.title === state[index].title) {
                          return {
                            ...item,
                            contents: {
                              ...item.contents,
                              [content.id]: value,
                            },
                          };
                        }
                        return item;
                      });
                      setState(newState);
                    }
                  }
                }}
                value={state[index].contents[content.id]}
              />
            </View>
          ))}
        </View>
      );
    },
    [state]
  );

  const renderSwitch = useMemo(() => {
    if (isDesktop()) {
      return (
        <View
          style={{
            flexWrap: 'wrap',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: 30,
          }}
        >
          {form.components.map((component, index) => {
            if (component.isFullWitdh) {
              return (
                <View
                  style={{ width: '100%', marginVertical: 10 }}
                  key={`${component.title}-${index}`}
                >
                  <View style={{ width: '45%' }}>
                    {component.title && (
                      <Text style={styles.componentHeader}>
                        {component.title}
                      </Text>
                    )}
                    {renderRowSwitch(component, index)}
                  </View>
                </View>
              );
            } else {
              return (
                <View
                  style={{ width: '45%', marginVertical: 10 }}
                  key={`${component.title}-${index}`}
                >
                  {component.title && (
                    <Text style={styles.componentHeader}>
                      {component.title}
                    </Text>
                  )}
                  {renderRowSwitch(component, index)}
                </View>
              );
            }
          })}
        </View>
      );
    }

    return form.components.map((component, index) => (
      <View style={styles.componentWrapper} key={`${component.title}-${index}`}>
        {component.title && (
          <Text style={styles.componentHeader}>{component.title}</Text>
        )}
        <View>{renderRowSwitch(component, index)}</View>
      </View>
    ));
  }, [form.components, isDesktop, renderRowSwitch]);

  return (
    <View>
      <View style={styles.header}>
        <Image
          source={form.icon}
          style={{
            width: 20,
            height: 20,
            resizeMode: 'contain',
            tintColor: STYLE_VARS.primaryColor,
            marginRight: 10,
          }}
        />
        <View style={{ alignItems: 'center', flexDirection: 'column' }}>
          <Text style={styles.headerText}>{form.title}</Text>
          <Text
            style={[
              styles.headerText,
              {
                fontSize: 20,
                fontWeight: '500',
              },
            ]}
          >
            Preferences
          </Text>
        </View>
      </View>
      {form.description && (
        <Text
          style={{
            fontSize: 14,
            letterSpacing: 1,
            fontFamily: FONTS.IowanOldStyleBTRoman,
            color: STYLE_VARS.fccDarkerGrey,
            textTransform: 'uppercase',
            marginBottom: 10,
          }}
        >
          {form.description}
        </Text>
      )}
      <View>{renderSwitch}</View>
      <View style={{ marginBottom: 20, alignItems: 'center' }}>
        <Button
          style={STYLE_VARS.blueButton}
          text="SAVE PREFERENCES"
          onPress={() => {
            if (onSubmit) {
              onSubmit(state);
            }
          }}
        />
      </View>
    </View>
  );
};

export default PreferencesForm;

const styles = StyleSheet.create({
  header: {
    flexDirection: 'row',
    marginTop: 20,
    alignItems: 'center',
    paddingBottom: 20,
    borderBottomColor: COLORS.DARK_GREY,
    borderBottomWidth: 0.5,
    marginBottom: 20,
  },
  headerText: {
    fontSize: 13,
    letterSpacing: 1,
    fontFamily: FONTS.IowanOldStyleBTRoman,
    color: COLORS.GREEN,
    textTransform: 'uppercase',
  },
  componentWrapper: {
    marginBottom: 20,
  },
  componentHeader: {
    fontSize: 16,
    letterSpacing: 1,
    fontFamily: FONTS.AvenirMedium,
    color: COLORS.GREEN,
    textTransform: 'uppercase',
    marginBottom: 20,
  },
  componentLabel: {
    fontSize: 14,
    letterSpacing: 1,
    fontFamily: FONTS.AvenirMedium,
    color: COLORS.DARKER_GREY,
    textTransform: 'uppercase',
  },
});
